import React from "react";
import { ChevronRight } from "lucide-react";
import { Card, CardContent, Button, Skeleton } from "../ui";

/**
 * Standard dashboard widget shell.
 * Single outer border from Card only — no stacked borders.
 */
export default function WidgetCard({
  title,
  icon: Icon,
  badge,
  viewAllPath,
  onViewAll,
  loading = false,
  empty = false,
  emptyText = "Nothing to show right now.",
  children,
  className = "",
}) {
  return (
    <Card
      className={`overflow-hidden transition-shadow duration-200 hover:shadow-md dark:hover:shadow-black/30 ${className}`}
    >
      <div className="flex items-center justify-between gap-2 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/80 dark:bg-neutral-900/80 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          {Icon && (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 dark:bg-primary-900/40 text-primary-600 dark:text-primary-300 ring-1 ring-primary-500/10 dark:ring-primary-400/20">
              <Icon className="h-4 w-4" strokeWidth={1.75} />
            </span>
          )}
          <span className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100 tracking-tight">
            {title}
          </span>
          {badge}
        </div>

        {(viewAllPath || onViewAll) && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onViewAll}
            className="group flex shrink-0 items-center gap-0.5 text-xs font-semibold text-primary-600 dark:text-primary-300 hover:text-primary-700 dark:hover:text-primary-200 hover:bg-primary-50 dark:hover:bg-primary-900/30 px-2 py-1 rounded-md"
          >
            <span>View all</span>
            <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        )}
      </div>

      <CardContent className="p-3.5 sm:p-4">
        {loading ? (
          <div className="space-y-2.5">
            <Skeleton height={44} width="100%" className="rounded-xl bg-neutral-100 dark:bg-neutral-800" />
            <Skeleton height={44} width="100%" className="rounded-xl bg-neutral-100 dark:bg-neutral-800" />
            <Skeleton height={44} width="100%" className="rounded-xl bg-neutral-100 dark:bg-neutral-800" />
          </div>
        ) : empty ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-500 mb-2">
              {Icon && <Icon className="h-5 w-5" strokeWidth={1.75} />}
            </div>
            <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">
              {emptyText}
            </p>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
